import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { useQueryParam } from "@shared/hookHelpers";

import TrackingNumbers from "./TrackingNumbers/Settings/TrackingNumbers";
import { getTrackingNumbers, getAgents } from "./TrackingNumbers/services";
import TrackingNumbersActivity from "./TrackingNumbers/TrackingNumbersActivity";
import TrackingNumbersLogs from "./TrackingNumbers/TrackingNumbersLogs";

import useTrackingNumberSettings from "./TrackingNumbers/Settings/useTrackingNumberSettings";
import useTrackingNumberActivity from "./TrackingNumbers/useTrackingNumberActivity";
import useTrackingNumberLogs from "./TrackingNumbers/useTrackingNumberLogs";
import { useCurrentUser } from "../reducers/layoutReducer/selectors";
import TrackingNumberModal from "./TrackingNumbers/Settings/TrackingNumberModal";

const PAGES = ["logs", "reporting", "settings"];

const mapTrackingNumberOptions = (trackingNumbers) =>
  trackingNumbers.map((tn) => ({
    label: tn.name,
    value: tn.id,
    meta: tn,
  }));

const PhoneSystem = () => {
  const currentUser = useCurrentUser();
  const [selectedPage, setSelectedPage] = useQueryParam("view", "logs");
  const [minDate, setMinDate] = useState(null);
  const [maxDate, setMaxDate] = useState(null);
  const [logFilters, setLogFilters] = useState({});
  const [trackingNumbers, setTrackingNumbers] = useState([]);
  const [selectedTrackingNumbers, setSelectedTrackingNumbers] = useState([]);
  const [agents, setAgents] = useState([]);
  const [offset, setOffset] = useState(0);
  const [textoffset, setTextOffset] = useState(0);
  const [sort, setSort] = useState(["id", "desc"]);
  const [showTrackingModal, setShowTrackingModal] = useState(false);
  const [numberToEdit, setNumberToEdit] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const addTrackingNumber = () => {
    setShowTrackingModal(true);
  };

  const closeModal = () => {
    setShowTrackingModal(false);
    setNumberToEdit(null);
  };

  const refreshPage = (view = "logs") => {
    const url = new URL(window.location.href);
    url.searchParams.set("view", view);
    window.location.href = url.toString();
  };

  const {
    agentsWithRoutingNumbers,
    trackingNumbers: settingsTrackingNumbers,
    sources,
    accountOwnerId,
    loadingSettings,
  } = useTrackingNumberSettings();

  const { activity, stats, loadingStats, loadingActivity } = useTrackingNumberActivity({
    selectedTrackingNumbers,
    trackingNumbers,
    minDate,
    maxDate,
  });

  const { callLogs, logsCount, textLogs, textlogsCount, loadingCalls, loadingTexts, loadingCounts } =
    useTrackingNumberLogs({
      selectedTrackingNumbers,
      trackingNumbers,
      minDate,
      maxDate,
      offset,
      textoffset,
      logFilters,
      sort,
    });

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const view = searchParams.get("view");
    if (view && PAGES.includes(view)) {
      setSelectedPage(view);
    }
  }, []);

  useEffect(() => {
    const load = async () => {
      try {
        setIsLoading(true);
        const [trackingNumbersResult, agentsResult] = await Promise.all([getTrackingNumbers(), getAgents()]);
        const mappedTrackingNumberOptions = mapTrackingNumberOptions(trackingNumbersResult);

        setTrackingNumbers(mappedTrackingNumberOptions);
        setSelectedTrackingNumbers(mappedTrackingNumberOptions);
        setAgents(agentsResult.agents);
      } finally {
        setIsLoading(false);
      }
    };

    load();
  }, []);

  const getSection = () => {
    switch (selectedPage) {
      case "settings":
        return (
          <TrackingNumbers
            accountOwnerId={accountOwnerId}
            agentsWithRoutingNumbers={agentsWithRoutingNumbers}
            loadingSettings={loadingSettings}
            sources={sources}
            trackingNumbers={settingsTrackingNumbers}
          />
        );
      case "reporting":
        return (
          <TrackingNumbersActivity
            isAdmin={currentUser.account.isAdmin}
            agents={agents}
            activity={activity}
            stats={stats}
            selectedTrackingNumbers={selectedTrackingNumbers}
            trackingNumbers={trackingNumbers}
            handleTrackingNumberFilterOnChange={(o) => setSelectedTrackingNumbers(o)}
            minDate={minDate}
            maxDate={maxDate}
            setMinDate={setMinDate}
            setMaxDate={setMaxDate}
            chosenView={selectedPage}
            logFilters={logFilters}
            setLogFilters={setLogFilters}
            showSettingsPage={() => setSelectedPage("settings")}
            loadingStats={loadingStats}
            loadingActivity={loadingActivity}
            isLoading={isLoading}
          />
        );
      case "logs":
      default:
        return (
          <TrackingNumbersLogs
            addTrackingNumber={addTrackingNumber}
            agents={agents}
            isAdmin={currentUser.account.isAdmin}
            sort={sort}
            setSort={setSort}
            offset={offset}
            setOffset={setOffset}
            textoffset={textoffset}
            setTextOffset={setTextOffset}
            logsCount={logsCount}
            textLogs={textLogs}
            textlogsCount={textlogsCount}
            callLogs={callLogs}
            selectedTrackingNumbers={selectedTrackingNumbers}
            trackingNumbers={trackingNumbers}
            handleTrackingNumberFilterOnChange={(o) => setSelectedTrackingNumbers(o)}
            minDate={minDate}
            maxDate={maxDate}
            setMinDate={setMinDate}
            setMaxDate={setMaxDate}
            chosenView={selectedPage}
            logFilters={logFilters}
            setLogFilters={setLogFilters}
            loadingCalls={loadingCalls}
            loadingTexts={loadingTexts}
            loadingCounts={loadingCounts}
          />
        );
    }
  };

  return (
    <div className="tw-flex tw-flex-col tw-h-full tw-overflow-y-auto tw-bg-tinted-gray-50">
      <div className="tw-flex tw-flex-row tw-gap-[40px] tw-ml-0 tw-sticky tw-top-0 tw-shadow tw-items-stretch tw-bg-white tw-z-10 tw-border-solid tw-border-b tw-border-b-gray-10 tw-border-t-0 tw-border-r-0 tw-border-l-0">
        <span className="tw-ml-32px tw-mt-8px tw-mb-12px tw-text-gray-75 tw-text-28px">Phone System</span>
        <div className="tw-flex tw-flex-row tw-gap-32px tw-mb-14px tw-items-end">
          <MenuOption
            name="Logs"
            value="logs"
            onClick={setSelectedPage}
            isSelected={selectedPage === "logs"}
            nonSelectedClassName="hover:!tw-bg-tinted-gray-50"
            btnClassName="hover:!tw-text-gray-75 "
          />
          <MenuOption
            name="Reporting"
            value="reporting"
            onClick={setSelectedPage}
            nonSelectedClassName="hover:!tw-text-gray-75 hover:!tw-bg-tinted-gray-50"
            btnClassName="hover:!tw-text-gray-75"
            isSelected={selectedPage === "reporting"}
          />
          <MenuOption
            name="Settings"
            value="settings"
            onClick={setSelectedPage}
            isSelected={selectedPage === "settings"}
            nonSelectedClassName="hover:!tw-text-gray-75 hover:!tw-bg-tinted-gray-50"
            btnClassName="hover:!tw-text-gray-75"
          />
        </div>
      </div>
      {getSection()}
      {showTrackingModal && (
        <TrackingNumberModal
          isNewNumber={numberToEdit === null}
          agentId={currentUser.id}
          agents={agentsWithRoutingNumbers}
          admins={agentsWithRoutingNumbers.filter(
            (person) => person.role === "Administrator" || person.role === "Account Owner",
          )}
          sources={sources}
          onClose={closeModal}
          trackingNumber={numberToEdit || {}}
          onNumbersChanged={refreshPage}
          isPlace={currentUser.account.isPlace}
        />
      )}
    </div>
  );
};

export default PhoneSystem;

const MenuOption = ({ name, value, onClick, isSelected, nonSelectedClassName, btnClassName }) => (
  <button
    type="button"
    className={`${isSelected ? "tw-text-gray-75 tw-bg-gray-lightest" : `tw-bg-transparent tw-text-gray-50 tw-cursor-pointer ${nonSelectedClassName}`} tw-border-0 tw-uppercase tw-font-bold tw-rounded-lg tw-px-10px tw-py-6px hover:tw-text-brand-warning ${btnClassName}`}
    onClick={() => onClick(value)}
  >
    {name}
  </button>
);

MenuOption.propTypes = {
  name: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired,
  isSelected: PropTypes.bool.isRequired,
  nonSelectedClassName: PropTypes.string,
  btnClassName: PropTypes.string,
};

MenuOption.defaultProps = {
  nonSelectedClassName: "",
  btnClassName: "",
};
